import { useEffect, useRef, useState } from "react";
import type { ImageState } from "../hooks/useImages";
import type { TemplateSettings } from "../lib/types";
import { renderTemplate, templateToBlob } from "../lib/imageProcessing";
import { downloadBlob, downloadFilename } from "../lib/utils";
import { contrastColor, symbolFor } from "../lib/symbols";
import SettingsPanel from "./SettingsPanel";
import InteractiveGrid from "./InteractiveGrid";
import ZoomControls, { zoomedCanvasStyle, type ZoomLevel } from "./ZoomControls";
import "./TemplateCard.css";

interface Props {
  image: ImageState;
  onSettingsChange: (patch: Partial<TemplateSettings> | TemplateSettings) => void;
  onToggleCell: (cellIdx: number) => void;
  onResetProgress: () => void;
  onRemove: () => void;
}

export default function TemplateCard({
  image,
  onSettingsChange,
  onToggleCell,
  onResetProgress,
  onRemove,
}: Props) {
  const previewRef = useRef<HTMLCanvasElement>(null);
  const [markMode, setMarkMode] = useState(false);
  const [highlight, setHighlight] = useState<number | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [zoom, setZoom] = useState<ZoomLevel>("fit");
  const [previewW, setPreviewW] = useState<number | undefined>(undefined);

  // Render static preview to the canvas. `markMode` is in the deps because flipping
  // back from mark mode unmounts and remounts this canvas; without it, the new canvas
  // would stay blank until something else changed.
  useEffect(() => {
    if (markMode) return;
    if (!image.result || !previewRef.current) return;
    const canvas = previewRef.current;
    renderTemplate(canvas, image.result, image.settings, image.progress);
    setPreviewW(canvas.width);
  }, [image.result, image.settings, image.progress, markMode]);

  const handleDownload = async () => {
    if (!image.result) return;
    setDownloading(true);
    try {
      const blob = await templateToBlob(image.result, image.settings, image.progress);
      downloadBlob(blob, downloadFilename(image.fileName, image.settings));
    } finally {
      setDownloading(false);
    }
  };

  const totalCells = image.settings.grid * image.settings.grid;
  const doneCount = image.progress.size;

  return (
    <article className="template-card card">
      <header className="template-card-header">
        <div className="template-card-title">
          <strong>{image.fileName}</strong>
          <span className="muted small">
            {image.settings.grid}×{image.settings.grid} ·{" "}
            {image.result ? `${image.result.palette.length} colors` : "processing…"}
            {doneCount > 0 && ` · ${doneCount}/${totalCells} done`}
          </span>
        </div>
        <div className="row">
          <button
            className="button secondary"
            onClick={() => setShowSettings((v) => !v)}
          >
            {showSettings ? "Hide settings" : "Settings"}
          </button>
          <button className="button ghost" onClick={onRemove} title="Remove">✕</button>
        </div>
      </header>

      {showSettings && (
        <div className="template-card-settings">
          <SettingsPanel
            settings={image.settings}
            onChange={onSettingsChange}
            compact
          />
        </div>
      )}

      <div className="template-card-body">
        <div className="template-preview">
          {image.error && <div className="error">⚠ {image.error}</div>}
          {!image.result && !image.error && (
            <div className="muted">Generating template…</div>
          )}
          {image.result && (
            <>
              <div className="template-preview-toolbar">
                <ZoomControls zoom={zoom} onChange={setZoom} />
                <span className="muted small zoom-hint">
                  Scroll or pinch to pan when zoomed.
                </span>
              </div>
              {markMode ? (
                <InteractiveGrid
                  result={image.result}
                  settings={image.settings}
                  progress={image.progress}
                  highlightIndex={highlight}
                  zoom={zoom}
                  onToggleCell={onToggleCell}
                />
              ) : (
                <div className="zoom-scroll-area">
                  <canvas
                    ref={previewRef}
                    className="preview-canvas"
                    style={zoomedCanvasStyle(zoom, previewW)}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {image.result && (
          <aside className="template-side">
            <div className="row">
              <button
                className={`button ${markMode ? "" : "secondary"}`}
                onClick={() => setMarkMode((v) => !v)}
              >
                {markMode ? "Stop marking" : "Mark while crocheting"}
              </button>
            </div>
            <ul className="palette-list">
              {image.result.palette.map((rgb, i) => {
                const [r, g, b] = rgb;
                const isHl = highlight === i;
                return (
                  <li
                    key={i}
                    className={`palette-item${isHl ? " highlighted" : ""}`}
                    onClick={() =>
                      setHighlight((cur) => (cur === i ? null : i))
                    }
                    title="Click to highlight only this color in the grid"
                  >
                    <span
                      className="swatch"
                      style={{
                        background: `rgb(${r},${g},${b})`,
                        color: contrastColor(r, g, b),
                      }}
                    >
                      {image.settings.showSymbols ? symbolFor(i) : i + 1}
                    </span>
                    <span className="palette-meta">
                      <strong>Color {i + 1}</strong>
                      <span className="muted small">
                        #{r.toString(16).padStart(2, "0").toUpperCase()}
                        {g.toString(16).padStart(2, "0").toUpperCase()}
                        {b.toString(16).padStart(2, "0").toUpperCase()}
                        {" · "}
                        {image.result!.counts[i]} cells
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>

            <div className="row">
              <button
                className="button"
                onClick={handleDownload}
                disabled={downloading}
              >
                {downloading ? "Saving…" : "Download PNG"}
              </button>
              {doneCount > 0 && (
                <button className="button ghost" onClick={onResetProgress}>
                  Reset progress
                </button>
              )}
            </div>
          </aside>
        )}
      </div>
    </article>
  );
}
